import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  CreditCard,
  Crown,
  Building2,
  Zap,
  Check,
  X,
  Loader2,
  ArrowRight,
  Shield,
  Clock,
  Receipt,
} from "lucide-react";

const PLAN_ICONS = { free: Zap, pro: Crown, enterprise: Building2 };
const PLAN_COLORS = {
  free: "cyan", pro: "amber", enterprise: "violet"
};

export default function BillingPage() {
  const { api, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [subscription, setSub] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [usage, setUsage] = useState({});
  const [transactions, setTransactions] = useState([]);
  const [features, setFeatures] = useState({});
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [pollingSession, setPollingSession] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [plansRes, subRes, txRes, featRes] = await Promise.all([
        api().get("/billing/plans"),
        api().get("/billing/subscription"),
        api().get("/billing/transactions"),
        api().get("/features/my-access"),
      ]);
      setPlans(plansRes.data);
      setSub(subRes.data.subscription);
      setCurrentPlan(subRes.data.plan);
      setUsage(subRes.data.usage);
      setTransactions(txRes.data);
      setFeatures(featRes.data.features);
    } catch (e) {
      toast.error("Failed to load billing data");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { loadData(); }, [loadData]);

  // Poll for payment status after Stripe redirect
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId && !pollingSession) {
      setPollingSession(sessionId);
      let attempts = 0;
      const poll = async () => {
        if (attempts >= 8) {
          toast.info("Payment status check timed out. Refresh to check again.");
          setSearchParams({});
          setPollingSession(null);
          return;
        }
        try {
          const res = await api().get(`/billing/checkout/status/${sessionId}`);
          if (res.data.payment_status === "paid") {
            toast.success(`Upgraded to ${res.data.plan_id?.toUpperCase()} plan!`);
            setSearchParams({});
            setPollingSession(null);
            loadData();
            return;
          } else if (res.data.status === "expired") {
            toast.error("Payment session expired.");
            setSearchParams({});
            setPollingSession(null);
            return;
          }
        } catch (e) { /* continue polling */ }
        attempts++;
        setTimeout(poll, 2500);
      };
      poll();
    }
  }, [searchParams, api, pollingSession, loadData, setSearchParams]);

  const handleCheckout = async (planId) => {
    setCheckoutLoading(planId);
    try {
      const res = await api().post("/billing/checkout", {
        plan_id: planId,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setCheckoutLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-[var(--hig-accent)]" />
      </div>
    );
  }

  const isTrial = subscription?.trial_end &&
    new Date(subscription.trial_end) > new Date();
  const isBypass = subscription?.bypass;
  const activePlanId = subscription?.plan_id || "free";

  return (
    <div className="space-y-8 max-w-5xl" data-testid="billing-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Billing & Plan</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Manage your subscription and feature access
        </p>
      </div>

      {pollingSession && (
        <div className="glass-card rounded-xl p-4 flex items-center gap-3 border-[var(--hig-accent)]/30">
          <Loader2 className="w-5 h-5 animate-spin text-[var(--hig-accent)]" />
          <span className="text-sm">Verifying your payment...</span>
        </div>
      )}

      {/* Current Plan Card */}
      <div className="glass-card rounded-xl p-6" data-testid="current-plan-card">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl bg-${PLAN_COLORS[activePlanId]}-500/20 flex items-center justify-center`}>
              {(() => { const Icon = PLAN_ICONS[activePlanId] || Zap; return <Icon className={`w-6 h-6 text-${PLAN_COLORS[activePlanId]}-400`} />; })()}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold">{currentPlan?.name || "Free"} Plan</h2>
                {isTrial && <Badge variant="outline" className="text-emerald-600 dark:text-emerald-400 border-emerald-500/30"><Clock className="w-3 h-3 mr-1" />Trial</Badge>}
                {isBypass && <Badge variant="outline" className="text-violet-600 dark:text-violet-400 border-violet-500/30"><Shield className="w-3 h-3 mr-1" />Bypass</Badge>}
              </div>
              <p className="text-sm text-muted-foreground">
                {currentPlan?.price_monthly > 0 ? `$${currentPlan.price_monthly}/mo` : "No charge"}
                {isTrial && ` · Trial ends ${new Date(subscription.trial_end).toLocaleDateString()}`}
                {isBypass && ` · ${subscription.bypass_reason || "Special access"}`}
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs text-muted-foreground">Scans this month</p>
            <p className="text-xl font-bold">
              {usage.scans_this_month || 0}
              {currentPlan?.features?.max_scans_per_month > 0 && (
                <span className="text-sm text-muted-foreground font-normal">
                  /{currentPlan.features.max_scans_per_month}
                </span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Plan Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="plan-cards">
        {plans.map((plan) => {
          const isActive = plan.plan_id === activePlanId;
          const color = PLAN_COLORS[plan.plan_id] || "cyan";
          const Icon = PLAN_ICONS[plan.plan_id] || Zap;
          const featureList = Object.entries(plan.features || {}).filter(
            ([k]) => !["max_scans_per_month", "max_credentials"].includes(k)
          );

          return (
            <div
              key={plan.plan_id}
              className={`glass-card rounded-xl p-6 flex flex-col transition-all duration-200 ${
                isActive ? `ring-2 ring-${color}-500/50` : "hover:border-[var(--n-border)]"
              }`}
              data-testid={`plan-card-${plan.plan_id}`}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 rounded-lg bg-${color}-500/20 flex items-center justify-center`}>
                  <Icon className={`w-5 h-5 text-${color}-400`} />
                </div>
                <div>
                  <h3 className="font-semibold">{plan.name}</h3>
                  {isActive && <Badge className={`bg-${color}-500/20 text-${color}-400 text-[10px]`}>Current</Badge>}
                </div>
              </div>

              <div className="mb-4">
                <span className="text-3xl font-bold">
                  ${plan.price_monthly?.toFixed(0) || "0"}
                </span>
                <span className="text-sm text-muted-foreground">/mo</span>
              </div>

              <div className="space-y-2 flex-1 mb-6">
                <div className="text-xs text-muted-foreground mb-1">
                  {plan.features?.max_scans_per_month === -1
                    ? "Unlimited scans"
                    : `${plan.features?.max_scans_per_month} scans/month`}
                  {" · "}
                  {plan.features?.max_credentials === -1
                    ? "Unlimited credentials"
                    : `${plan.features?.max_credentials} credentials`}
                </div>
                {featureList.map(([key, val]) => (
                  <div key={key} className="flex items-center gap-2 text-sm">
                    {val ? (
                      <Check className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
                    ) : (
                      <X className="w-3.5 h-3.5 text-zinc-600 shrink-0" />
                    )}
                    <span className={val ? "text-[var(--n-text-secondary)]" : "text-zinc-600"}>
                      {key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                    </span>
                  </div>
                ))}
              </div>

              {isActive ? (
                <Button disabled className="w-full" variant="outline">
                  Current Plan
                </Button>
              ) : plan.price_monthly > 0 ? (
                <Button
                  className={`w-full bg-${color}-500 hover:bg-${color}-600 text-black`}
                  onClick={() => handleCheckout(plan.plan_id)}
                  disabled={checkoutLoading === plan.plan_id}
                  data-testid={`upgrade-${plan.plan_id}-btn`}
                >
                  {checkoutLoading === plan.plan_id ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <ArrowRight className="w-4 h-4 mr-2" />
                  )}
                  {activePlanId !== "free" && plan.price_monthly > (currentPlan?.price_monthly || 0)
                    ? "Upgrade"
                    : "Get Started"}
                </Button>
              ) : (
                <Button disabled variant="outline" className="w-full">
                  Free Forever
                </Button>
              )}
            </div>
          );
        })}
      </div>

      {/* Feature Access */}
      <div className="glass-card rounded-xl p-6" data-testid="feature-access-card">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5 text-[var(--hig-accent)]" />
          Feature Access
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {Object.entries(features).map(([key, feat]) => (
            <div
              key={key}
              className={`flex items-center justify-between p-3 rounded-lg border ${
                feat.allowed
                  ? "border-emerald-500/20 bg-emerald-500/5"
                  : "border-zinc-700/50 bg-zinc-900/50"
              }`}
              data-testid={`feature-${key}`}
            >
              <div>
                <p className="text-sm font-medium">{feat.display_name}</p>
                <p className="text-xs text-muted-foreground">{feat.description}</p>
              </div>
              {feat.allowed ? (
                <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
              ) : (
                <Badge variant="outline" className="text-xs text-amber-600 dark:text-amber-400 border-amber-500/30 shrink-0">
                  {feat.required_plan?.toUpperCase()}
                </Badge>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Transaction History */}
      {transactions.length > 0 && (
        <div className="glass-card rounded-xl p-6" data-testid="transaction-history">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Receipt className="w-5 h-5 text-[var(--hig-accent)]" />
            Payment History
          </h3>
          <div className="space-y-2">
            {transactions.map((tx, i) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-[var(--n-hover)] border border-[var(--n-border)]">
                <div>
                  <p className="text-sm font-medium capitalize">{tx.plan_id} Plan</p>
                  <p className="text-xs text-muted-foreground">{new Date(tx.created_at).toLocaleDateString()}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-mono">${tx.amount?.toFixed(2)}</p>
                  <Badge
                    variant="outline"
                    className={
                      tx.payment_status === "paid"
                        ? "text-emerald-600 dark:text-emerald-400 border-emerald-500/30"
                        : tx.payment_status === "expired"
                        ? "text-red-600 dark:text-red-400 border-red-500/30"
                        : "text-amber-600 dark:text-amber-400 border-amber-500/30"
                    }
                  >
                    {tx.payment_status}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
